import { DependencyList, EffectCallback, useEffect, useRef, useState } from 'react'
import { BehaviorSubject, Observable, Subject } from 'rxjs'

import { getRandomId } from '@/utils/utilities'

// Hook
export function useDebounce<T>(value: T, delay = 250) {
	// State and setters for debounced value
	const [debouncedValue, setDebouncedValue] = useState(value)
	useEffect(
		() => {
			// Update debounced value after delay
			const handler = setTimeout(() => {
				setDebouncedValue(value)
			}, delay)
			// Cancel the timeout if value changes (also on delay change or unmount)
			// This is how we prevent debounced value from updating if value is changed ...
			// .. within the delay period. Timeout gets cleared and restarted.
			return () => {
				clearTimeout(handler)
			}
		},
		[value, delay] // Only re-call effect if value or delay changes
	)
	return debouncedValue
}

export function useDebounceEffect(effect: EffectCallback, deps: DependencyList, delay = 250) {
	const destructorRef = useRef<(() => void) | null>(null)
	if (!deps) {
		throw new Error('useDebounceEffect requires deps')
	}
	useEffect(
		() => {
			// Update debounced value after delay
			const handler = setTimeout(() => {
				if (destructorRef.current) {
					destructorRef.current()
					destructorRef.current = null
				}
				const destructor = effect()
				if (destructor) {
					destructorRef.current = destructor
				}
			}, delay)
			// Cancel the timeout if value changes (also on delay change or unmount)
			// This is how we prevent debounced value from updating if value is changed ...
			// .. within the delay period. Timeout gets cleared and restarted.
			return () => {
				clearTimeout(handler)
			}
		},
		[...deps, delay] // Only re-call effect if value or delay changes
	)
}

export function useInterval(callback: () => void, delay: number) {
	const savedCallback = useRef<() => void>(undefined)

	// Remember the latest callback.
	useEffect(() => {
		savedCallback.current = callback
	}, [callback])

	// Set up the interval.
	useEffect(() => {
		function tick() {
			savedCallback.current?.()
		}

		if (delay > 0) {
			let id = setInterval(tick, delay)
			return () => clearInterval(id)
		}
	}, [delay])
}

export function useUniqueId() {
	const [id] = useState(getRandomId())
	return id
}

interface HandlerParam {
	url: string
}

export function useWatchObservable<T>(subject: Observable<T>, callback: (value: T) => void, deps: any[]) {
	if (!deps) {
		throw new Error('useWatchObservable requires deps')
	}
	useEffect(() => {
		const subscription = subject.subscribe(callback)
		return () => subscription.unsubscribe()
	}, deps)
}

export function useCreateBehaviorSubject<T>(initialValue: T): BehaviorSubject<T> {
	return useState(new BehaviorSubject(initialValue))[0]
}

export function useCreateAndWatchBehaviorSubject<T>(initialValue: T): [T, BehaviorSubject<T>] {
	const subject = useState(new BehaviorSubject(initialValue))[0]
	const value = useGetSubject(subject)
	return [value, subject]
}

export function useCreateSubject<T>(): Subject<T> {
	return useState(new Subject<T>())[0]
}

export function useGetSubject<T>(subject: BehaviorSubject<T>): T {
	return useGetObservable(subject, subject.value)
}

export function useGetObservable<T>(observable: Observable<T>, initialValue: T): T {
	const [value, setValue] = useState(initialValue)
	useEffect(() => {
		const subscription = observable.subscribe(setValue)
		return () => subscription.unsubscribe()
	}, [])
	return value
}

export function useAsyncEffect(effect: () => Promise<void>, deps?: DependencyList) {
	useEffect(() => {
		effect?.()
	}, deps)
}
